import { ReferentRows } from '@/components/admin/versioned/referent-rows';
import {
  VersionedDialog,
  VersionedDialogSection,
} from '@/components/admin/versioned/versioned-dialog';
import type { VersionedWording } from '@/lib/versioned-labels';
import type { UsageLine } from '@matter_extractor/shared';
import { Alert } from '@pms-digital/design-system';

/**
 * The retirement warning (refonte modulaire §4).
 *
 * ## It lists the referents, and it does not offer to stop
 *
 * §4: « le retrait reste toujours possible, même quand une version en
 * production cite l'objet ; en contrepartie, le référent porte un
 * avertissement qui dit quoi faire ("remplacer le critère X, retiré le ...") et
 * qui ne disparaît qu'une fois la référence remplacée ». So this box has no
 * refusal to word: it names every protocol and experience that will go
 * « invalide », says what warning they will carry, and lets the operator
 * through. A dialog that blocked here would leave an entity nobody can retire
 * because something nobody uses still cites it.
 *
 * Both tags are shown per referent, unlike the pointer box which filters: a
 * retirement hits `production` and `développement` at once, so splitting the
 * list in two would suggest a choice that does not exist.
 *
 * The two sentences that soften it are true and belong here: the past sessions
 * keep their freeze, and the restoration is one gesture away in the history.
 *
 * ## When the entity has a version on the prod (vitrine.md §4.1, H13, issue #192)
 *
 * The retirement keeps its meaning - a retired entity resolves nowhere, pinned or `latest` - but it
 * goes through the prod FIRST. The box then splits what it lists: on the prod, the referents whose
 * versions the prod plays and that CEASE TO RESOLVE; on the dev, the ones that become invalid. And it
 * says the one thing that is new: the dev moves only if the prod applied, and if the prod does not
 * answer nothing changes on either side.
 */

export interface RetireDialogProps {
  wording: VersionedWording;
  name: string;
  usage: readonly UsageLine[];
  /**
   * The retirement goes through the prod: a prod is linked and the entity has a version there. The
   * box then lists the prod apart from the dev, and says it runs on the prod first.
   */
  viaProd?: boolean;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function RetireDialog({
  wording,
  name,
  usage,
  viaProd = false,
  busy,
  error,
  onConfirm,
  onCancel,
}: RetireDialogProps) {
  // An object with one side only (npc.md N5) is cited by nothing: the sections below would say
  // « rien ne le cite » to a question nobody asked. What the box says instead is what hiding does.
  if (wording.developmentOnly === true) {
    return (
      <VersionedDialog
        data-testid={`${wording.entity}-boite-retrait`}
        title={`Retirer « ${name} » ?`}
        description="Il disparaît de la bibliothèque, des sélecteurs de lancement et du choix des bancs."
        confirmLabel={wording.retirement.confirmLabel}
        confirmTone="destructive"
        busy={busy}
        error={error}
        onConfirm={onConfirm}
        onCancel={onCancel}
      >
        <Alert tone="info">
          Rien n’est effacé : ses versions et ses rejeux restent lisibles, les bancs qui le
          portaient le gardent et leur lancement le saute, et la restauration reste possible depuis
          l’historique.
        </Alert>
      </VersionedDialog>
    );
  }

  return (
    <VersionedDialog
      data-testid={`${wording.entity}-boite-retrait`}
      title={`Retirer « ${name} » ?`}
      description={
        viaProd
          ? 'Il disparaît des sélecteurs. Retiré, il ne se résout plus nulle part, ni sur le dev ni sur la prod, qu’on le cite épinglé ou en latest.'
          : 'Il disparaît des sélecteurs. Les protocoles et les expériences qui le citent passent en état « invalide ».'
      }
      confirmLabel={wording.retirement.confirmLabel}
      confirmTone="destructive"
      busy={busy}
      error={error}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {viaProd ? (
        <VersionedDialogSection title="Sur la prod, cessent de se résoudre">
          <ReferentRows
            entity={wording.entity}
            usage={usage}
            tag="production"
            emptyMessage="Rien de ce que la prod joue ne le cite."
          />
        </VersionedDialogSection>
      ) : null}

      <VersionedDialogSection
        title={viaProd ? 'Sur le dev, deviennent invalides' : 'Ce qui devient invalide'}
      >
        {/*
          `tag="all"`: a retirement hits `production` AND `développement` at
          once, so splitting the list the way the pointer box does would suggest
          a choice that does not exist here.

          And the empty sentence SURVIVES here, where the card and the editor
          panel dropped theirs (§4): this box answers « qui joue encore ceci »,
          and « rien ne le cite » is an answer to that - not the zero count a
          fresh card would have carried.
        */}
        <ReferentRows
          entity={wording.entity}
          usage={
            viaProd ? usage.filter((referent) => referent.tags.includes('development')) : usage
          }
          tag={viaProd ? 'development' : 'all'}
          emptyMessage="Rien ne le cite : aucun référent ne devient invalide."
        />
      </VersionedDialogSection>

      <Alert tone="warning" title={wording.retirement.warningTitle(name)}>
        L’avertissement ne disparaît qu’une fois la référence remplacée. Les sessions déjà jouées
        gardent leur gel, et la restauration reste possible depuis l’historique.
      </Alert>

      {viaProd ? (
        <p
          data-testid={`${wording.entity}-retrait-par-la-prod`}
          className="rounded-md bg-bleu-nuit-tint px-3 py-2 text-caption text-gray-800 leading-4"
        >
          Le retrait passe d’abord par la prod : le dev ne bouge que si la prod l’a appliqué. Si
          elle ne répond pas, rien ne change, ni d’un côté ni de l’autre.
        </p>
      ) : null}
    </VersionedDialog>
  );
}
